<template>
  <!-- This wrapper div is dumb, only exists to prevent warning during testing. -->
  <div>
    <div
      id="map"
      ref="map"
    />
    <InfoWindow
      v-if="isMapInitialized && infoWindowContents.length"
      data-testid="infowindow"
    />
  </div>
</template>

<script lang="ts">
// Services and Helpers
import {mapActions, mapState} from 'vuex';
import {MapService, MVTLayers} from '@/services/MapService';
import {EventService} from '@/services/EventService';
import {MENU_BAR_WIDTH_PIXELS} from '@/constants/MainMenu';

// Components
import InfoWindow from './InfoWindow/InfoWindow.vue';

export default {
  name: 'GoogleMap',
  components: {
    InfoWindow,
  },
  props: {
    loadOnIdle: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isMapInitialized: false,
      menuBarWidth: `${MENU_BAR_WIDTH_PIXELS}px`,
    };
  },
  computed: {
    ...mapState('infowindow', ['infoWindowContents']),
  },
  mounted() {
    this.initMap();
    this.isMapInitialized = true;
  },
  methods: {
    ...mapActions(['updateGeoHashRecord', 'loadOverlays']),
    /**
     *  When 'idle' map event is fired, this function is called to find overlays within
     *  map bounds if the map zoom level is >= 13, or removes overlays if zoomed out
     */
    handleIdle() {
      // Update the address bar as soon as the map settles.
      this.appendLocationParams();

      // We don't need to load overlays on idle in Shareable Maps because we load all features on page load
      if (this.loadOnIdle) {
        this.loadOverlays({targetTaxFeatures: false});
      }
    },
    initMap() {
      // Get the settings from the query if possible, or fallback to reasonable defaults.
      const {zoom = 5, lat = 39.34958, lng = -84.47265} = MapService.mapSettingsFromQuery(window.location.search);
      MapService.initializeMap(lat, lng, zoom, document.getElementById('map') as HTMLElement);

      // For esri vector tile layers to appear, we must initialize the MVTLayers GoogleMapsOverlay
      MVTLayers.initialize(MapService.getMap());

      // When the map idles, trigger handleIdle()
      MapService.addMapListener('mapIdleHandler', 'idle', this.handleIdle);

      // When a user clicks the map, run the centralized click handler
      MapService.addMapListener('centralClickHandler', 'click', (event) => {
        EventService.handleMapClick(event);
      });

      MapService.addMapListener('centralContextMenuHandler', 'contextmenu', (event) => {
        EventService.handleMapContextMenu(event);
      });
    },
    /**
     * Extracts the location and zoom from the current map state and appends or replaces
     * the query parameters with the updated location.
     */
    appendLocationParams() {
      const [href, query] = window.location.href.split('?');
      const params = MapService.mapSettingsToQuery(query);
      history.replaceState({}, '', `${href}?${params.toString()}`);
    },
  },
};
</script>
<style lang="scss"> // eslint-disable-line vue-scoped-css/enforce-style-type
img.overlay {
  width: 100%;
  height: 100%;
}
.crosshair div.overlay, .crosshair svg .overlay {
  cursor: crosshair;
}
.pencil div.overlay, .pencil svg .overlay {
  cursor: url('../assets/pencil.png') 0 25, auto;
}
.grab div.overlay, .grab path.overlay {
  cursor: grab;
}
.text, .text * {
  cursor: text !important;
}
.move, .move * {
  cursor: move !important;
}
div.overlay, svg .overlay {
  /* z-index here is primarily used for stacking image graphics above other feature types */
  z-index: 9999;
  position: absolute;
  cursor: pointer;
}
</style>

<style lang="scss" scoped>
#map {
  position: absolute;
  height: 100vh;
  width: 100vw;

  @include main.respond(desktop) {
    // Calculate width plus static menu bar for desktop
    width: calc(100vw - v-bind(menuBarWidth));
    margin-left: v-bind(menuBarWidth);
  }
}
</style>
